import type { ReactNode } from 'react';

export type Country = {
  label: string;
  value: string;
  phoneCode: string;
  currency: string;
  currencySymbol: string;
  flag: string;
};

export const mapCurrencyToCountry = (countries: Country[], currency: string) => {
  if (currency === "USD") {
    return {
      label: 'United States',
      value: 'US',
      phoneCode: '+1',
      currency: 'USD',
      currencySymbol: '$',
      flag: '🇺🇸',
    }
  }
  if (currency === "EUR") {
    return {
      label: "Europe",
      value: "EU",
      phoneCode: "+49",
      currency: "EUR",
      currencySymbol: "€",
      flag: "🇪🇺",
    }
  }

  return countries.find(country => country.currency === currency)
}

export const countries = [
  {
    label: 'Afghanistan',
    value: 'AF',
    phoneCode: '+93',
    currency: 'AFN',
    currencySymbol: 'AFN؋', // Prefixed Afghani symbol
    flag: '🇦🇫',
  },
  {
    label: 'Albania',
    value: 'AL',
    phoneCode: '+355',
    currency: 'ALL',
    currencySymbol: 'ALL L', // Prefixed Lek symbol
    flag: '🇦🇱',
  },
  {
    label: 'Algeria',
    value: 'DZ',
    phoneCode: '+213',
    currency: 'DZD',
    currencySymbol: 'DZD د.ج', // Prefixed Algerian Dinar (Arabic script)
    flag: '🇩🇿',
  },
  {
    label: 'Andorra',
    value: 'AD',
    phoneCode: '+376',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed, universally unique)
    flag: '🇦🇩',
  },
  {
    label: 'Angola',
    value: 'AO',
    phoneCode: '+244',
    currency: 'AOA',
    currencySymbol: 'AOA Kz', // Prefixed Kwanza symbol
    flag: '🇦🇴',
  },
  {
    label: 'Antigua and Barbuda',
    value: 'AG',
    phoneCode: '+1-268',
    currency: 'XCD',
    currencySymbol: 'XCD$', // Prefixed Eastern Caribbean Dollar
    flag: '🇦🇬',
  },
  {
    label: 'Argentina',
    value: 'AR',
    phoneCode: '+54',
    currency: 'ARS',
    currencySymbol: 'AR$', // Prefixed Argentine Peso
    flag: '🇦🇷',
  },
  {
    label: 'Armenia',
    value: 'AM',
    phoneCode: '+374',
    currency: 'AMD',
    currencySymbol: 'AMD֏', // Prefixed Armenian Dram symbol
    flag: '🇦🇲',
  },
  {
    label: 'Australia',
    value: 'AU',
    phoneCode: '+61',
    currency: 'AUD',
    currencySymbol: 'A$', // Prefixed Australian Dollar
    flag: '🇦🇺',
  },
  {
    label: 'Austria',
    value: 'AT',
    phoneCode: '+43',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇦🇹',
  },
  {
    label: 'Azerbaijan',
    value: 'AZ',
    phoneCode: '+994',
    currency: 'AZN',
    currencySymbol: 'AZN₼', // Prefixed Azerbaijani Manat symbol
    flag: '🇦🇿',
  },
  {
    label: 'Bahamas',
    value: 'BS',
    phoneCode: '+1-242',
    currency: 'BSD',
    currencySymbol: 'B$', // Prefixed Bahamian Dollar
    flag: '🇧🇸',
  },
  {
    label: 'Bahrain',
    value: 'BH',
    phoneCode: '+973',
    currency: 'BHD',
    currencySymbol: 'BHD .د.ب', // Prefixed Bahraini Dinar (Arabic script)
    flag: '🇧🇭',
  },
  {
    label: 'Bangladesh',
    value: 'BD',
    phoneCode: '+880',
    currency: 'BDT',
    currencySymbol: 'BDT৳', // Prefixed Bangladeshi Taka symbol
    flag: '🇧🇩',
  },
  {
    label: 'Barbados',
    value: 'BB',
    phoneCode: '+1-246',
    currency: 'BBD',
    currencySymbol: 'BBD$', // Prefixed Barbadian Dollar
    flag: '🇧🇧',
  },
  {
    label: 'Belarus',
    value: 'BY',
    phoneCode: '+375',
    currency: 'BYN',
    currencySymbol: 'BYN Br', // Prefixed Belarusian Rubles symbol
    flag: '🇧🇾',
  },
  {
    label: 'Belgium',
    value: 'BE',
    phoneCode: '+32',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇧🇪',
  },
  {
    label: 'Belize',
    value: 'BZ',
    phoneCode: '+501',
    currency: 'BZD',
    currencySymbol: 'BZ$', // Prefixed Belize Dollar
    flag: '🇧🇿',
  },
  {
    label: 'Benin',
    value: 'BJ',
    phoneCode: '+229',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇧🇯',
  },
  {
    label: 'Bhutan',
    value: 'BT',
    phoneCode: '+975',
    currency: 'BTN',
    currencySymbol: 'BTN Nu.', // Prefixed Bhutanese Ngultrum symbol
    flag: '🇧🇹',
  },
  {
    label: 'Bolivia',
    value: 'BO',
    phoneCode: '+591',
    currency: 'BOB',
    currencySymbol: 'BOB Bs.', // Prefixed Bolivian Boliviano symbol
    flag: '🇧🇴',
  },
  {
    label: 'Bosnia and Herzegovina',
    value: 'BA',
    phoneCode: '+387',
    currency: 'BAM',
    currencySymbol: 'BAM KM', // Prefixed Bosnia-Herzegovina Convertible Mark symbol
    flag: '🇧🇦',
  },
  {
    label: 'Botswana',
    value: 'BW',
    phoneCode: '+267',
    currency: 'BWP',
    currencySymbol: 'BWP P', // Prefixed Botswana Pula symbol
    flag: '🇧🇼',
  },
  {
    label: 'Brazil',
    value: 'BR',
    phoneCode: '+55',
    currency: 'BRL',
    currencySymbol: 'R$', // Prefixed Brazilian Real (commonly used as R$)
    flag: '🇧🇷',
  },
  {
    label: 'Brunei',
    value: 'BN',
    phoneCode: '+673',
    currency: 'BND',
    currencySymbol: 'B$', // Prefixed Brunei Dollar
    flag: '🇧🇳',
  },
  {
    label: 'Bulgaria',
    value: 'BG',
    phoneCode: '+359',
    currency: 'BGN',
    currencySymbol: 'BGN лв', // Prefixed Bulgarian Lev symbol
    flag: '🇧🇬',
  },
  {
    label: 'Burkina Faso',
    value: 'BF',
    phoneCode: '+226',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇧🇫',
  },
  {
    label: 'Burundi',
    value: 'BI',
    phoneCode: '+257',
    currency: 'BIF',
    currencySymbol: 'BIF FBu', // Prefixed Burundian Franc symbol
    flag: '🇧🇮',
  },
  {
    label: 'Cabo Verde',
    value: 'CV',
    phoneCode: '+238',
    currency: 'CVE',
    currencySymbol: 'CVE$', // Prefixed Cape Verdean Escudo
    flag: '🇨🇻',
  },
  {
    label: 'Cambodia',
    value: 'KH',
    phoneCode: '+855',
    currency: 'KHR',
    currencySymbol: 'KHR៛', // Prefixed Cambodian Riel symbol
    flag: '🇰🇭',
  },
  {
    label: 'Cameroon',
    value: 'CM',
    phoneCode: '+237',
    currency: 'XAF',
    currencySymbol: 'XAF CFA', // Prefixed Central African CFA Franc
    flag: '🇨🇲',
  },
  {
    label: 'Canada',
    value: 'CA',
    phoneCode: '+1',
    currency: 'CAD',
    currencySymbol: 'C$', // Prefixed Canadian Dollar
    flag: '🇨🇦',
  },
  {
    label: 'Central African Republic',
    value: 'CF',
    phoneCode: '+236',
    currency: 'XAF',
    currencySymbol: 'XAF CFA', // Prefixed Central African CFA Franc
    flag: '🇨🇫',
  },
  {
    label: 'Chad',
    value: 'TD',
    phoneCode: '+235',
    currency: 'XAF',
    currencySymbol: 'XAF CFA', // Prefixed Central African CFA Franc
    flag: '🇹🇩',
  },
  {
    label: 'Chile',
    value: 'CL',
    phoneCode: '+56',
    currency: 'CLP',
    currencySymbol: 'CL$', // Prefixed Chilean Peso
    flag: '🇨🇱',
  },
  {
    label: 'China',
    value: 'CN',
    phoneCode: '+86',
    currency: 'CNY',
    currencySymbol: 'CN¥', // Prefixed Chinese Yuan (¥ with CN prefix)
    flag: '🇨🇳',
  },
  {
    label: 'Colombia',
    value: 'CO',
    phoneCode: '+57',
    currency: 'COP',
    currencySymbol: 'CO$', // Prefixed Colombian Peso
    flag: '🇨🇴',
  },
  {
    label: 'Comoros',
    value: 'KM',
    phoneCode: '+269',
    currency: 'KMF',
    currencySymbol: 'KMF CF', // Prefixed Comorian Franc symbol
    flag: '🇰🇲',
  },
  {
    label: 'Congo (Congo-Brazzaville)',
    value: 'CG',
    phoneCode: '+242',
    currency: 'XAF',
    currencySymbol: 'XAF CFA', // Prefixed Central African CFA Franc
    flag: '🇨🇬',
  },
  {
    label: 'Congo (DRC)',
    value: 'CD',
    phoneCode: '+243',
    currency: 'CDF',
    currencySymbol: 'CDF FC', // Prefixed Congolese Franc symbol
    flag: '🇨🇩',
  },
  {
    label: 'Costa Rica',
    value: 'CR',
    phoneCode: '+506',
    currency: 'CRC',
    currencySymbol: 'CRC₡', // Prefixed Costa Rican Colón symbol
    flag: '🇨🇷',
  },
  {
    label: 'Croatia',
    value: 'HR',
    phoneCode: '+385',
    currency: 'EUR', // Updated from HRK to EUR (adopted Jan 1, 2023)
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇭🇷',
  },
  {
    label: 'Cuba',
    value: 'CU',
    phoneCode: '+53',
    currency: 'CUP',
    currencySymbol: 'CU$', // Prefixed Cuban Peso
    flag: '🇨🇺',
  },
  {
    label: 'Cyprus',
    value: 'CY',
    phoneCode: '+357',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇨🇾',
  },
  {
    label: 'Czechia',
    value: 'CZ',
    phoneCode: '+420',
    currency: 'CZK',
    currencySymbol: 'CZK Kč', // Prefixed Czech Koruna symbol
    flag: '🇨🇿',
  },
  {
    label: 'Denmark',
    value: 'DK',
    phoneCode: '+45',
    currency: 'DKK',
    currencySymbol: 'DKK kr', // Prefixed Danish Krone symbol
    flag: '🇩🇰',
  },
  {
    label: 'Djibouti',
    value: 'DJ',
    phoneCode: '+253',
    currency: 'DJF',
    currencySymbol: 'DJF Fdj', // Prefixed Djiboutian Franc symbol
    flag: '🇩🇯',
  },
  {
    label: 'Dominica',
    value: 'DM',
    phoneCode: '+1-767',
    currency: 'XCD',
    currencySymbol: 'XCD$', // Prefixed Eastern Caribbean Dollar
    flag: '🇩🇲',
  },
  {
    label: 'Dominican Republic',
    value: 'DO',
    phoneCode: '+1-809',
    currency: 'DOP',
    currencySymbol: 'RD$', // Prefixed Dominican Peso
    flag: '🇩🇴',
  },
  {
    label: 'Ecuador',
    value: 'EC',
    phoneCode: '+593',
    currency: 'USD',
    currencySymbol: 'US$', // Prefixed US Dollar
    flag: '🇪🇨',
  },
  {
    label: 'Egypt',
    value: 'EG',
    phoneCode: '+20',
    currency: 'EGP',
    currencySymbol: 'EGP £', // Prefixed Egyptian Pound (sometimes E£)
    flag: '🇪🇬',
  },
  {
    label: 'El Salvador',
    value: 'SV',
    phoneCode: '+503',
    currency: 'USD',
    currencySymbol: 'US$', // Prefixed US Dollar
    flag: '🇸🇻',
  },
  {
    label: 'Equatorial Guinea',
    value: 'GQ',
    phoneCode: '+240',
    currency: 'XAF',
    currencySymbol: 'XAF CFA', // Prefixed Central African CFA Franc
    flag: '🇬🇶',
  },
  {
    label: 'Eritrea',
    value: 'ER',
    phoneCode: '+291',
    currency: 'ERN',
    currencySymbol: 'ERN Nfk', // Prefixed Eritrean Nakfa symbol
    flag: '🇪🇷',
  },
  {
    label: 'Estonia',
    value: 'EE',
    phoneCode: '+372',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇪🇪',
  },
  {
    label: 'Eswatini',
    value: 'SZ',
    phoneCode: '+268',
    currency: 'SZL',
    currencySymbol: 'SZL L', // Prefixed Swazi Lilangeni symbol
    flag: '🇸🇿',
  },
  {
    label: 'Ethiopia',
    value: 'ET',
    phoneCode: '+251',
    currency: 'ETB',
    currencySymbol: 'ETB Br', // Prefixed Ethiopian Birr symbol
    flag: '🇪🇹',
  },
  {
    label: 'Fiji',
    value: 'FJ',
    phoneCode: '+679',
    currency: 'FJD',
    currencySymbol: 'FJ$', // Prefixed Fijian Dollar
    flag: '🇫🇯',
  },
  {
    label: 'Finland',
    value: 'FI',
    phoneCode: '+358',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇫🇮',
  },
  {
    label: 'France',
    value: 'FR',
    phoneCode: '+33',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇫🇷',
  },
  {
    label: 'Gabon',
    value: 'GA',
    phoneCode: '+241',
    currency: 'XAF',
    currencySymbol: 'XAF CFA', // Prefixed Central African CFA Franc
    flag: '🇬🇦',
  },
  {
    label: 'Gambia',
    value: 'GM',
    phoneCode: '+220',
    currency: 'GMD',
    currencySymbol: 'GMD D', // Prefixed Gambian Dalasi symbol
    flag: '🇬🇲',
  },
  {
    label: 'Georgia',
    value: 'GE',
    phoneCode: '+995',
    currency: 'GEL',
    currencySymbol: 'GEL ₾', // Prefixed Georgian Lari symbol
    flag: '🇬🇪',
  },
  {
    label: 'Germany',
    value: 'DE',
    phoneCode: '+49',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇩🇪',
  },
  {
    label: 'Ghana',
    value: 'GH',
    phoneCode: '+233',
    currency: 'GHS',
    currencySymbol: 'GHS ₵', // Prefixed Ghanaian Cedi symbol
    flag: '🇬🇭',
  },
  {
    label: 'Greece',
    value: 'GR',
    phoneCode: '+30',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇬🇷',
  },
  {
    label: 'Grenada',
    value: 'GD',
    phoneCode: '+1-473',
    currency: 'XCD',
    currencySymbol: 'XCD$', // Prefixed Eastern Caribbean Dollar
    flag: '🇬🇩',
  },
  {
    label: 'Guatemala',
    value: 'GT',
    phoneCode: '+502',
    currency: 'GTQ',
    currencySymbol: 'GTQ Q', // Prefixed Guatemalan Quetzal symbol
    flag: '🇬🇹',
  },
  {
    label: 'Guinea',
    value: 'GN',
    phoneCode: '+224',
    currency: 'GNF',
    currencySymbol: 'GNF FG', // Prefixed Guinean Franc symbol
    flag: '🇬🇳',
  },
  {
    label: 'Guinea-Bissau',
    value: 'GW',
    phoneCode: '+245',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇬🇼',
  },
  {
    label: 'Guyana',
    value: 'GY',
    phoneCode: '+592',
    currency: 'GYD',
    currencySymbol: 'GY$', // Prefixed Guyanese Dollar
    flag: '🇬🇾',
  },
  {
    label: 'Haiti',
    value: 'HT',
    phoneCode: '+509',
    currency: 'HTG',
    currencySymbol: 'HTG G', // Prefixed Haitian Gourde symbol
    flag: '🇭🇹',
  },
  {
    label: 'Honduras',
    value: 'HN',
    phoneCode: '+504',
    currency: 'HNL',
    currencySymbol: 'HNL L', // Prefixed Honduran Lempira symbol
    flag: '🇭🇳',
  },
  {
    label: 'Hungary',
    value: 'HU',
    phoneCode: '+36',
    currency: 'HUF',
    currencySymbol: 'HUF Ft', // Prefixed Hungarian Forint symbol
    flag: '🇭🇺',
  },
  {
    label: 'Iceland',
    value: 'IS',
    phoneCode: '+354',
    currency: 'ISK',
    currencySymbol: 'ISK kr', // Prefixed Icelandic Króna symbol
    flag: '🇮🇸',
  },
  {
    label: 'India',
    value: 'IN',
    phoneCode: '+91',
    currency: 'INR',
    currencySymbol: '₹', // Indian Rupee symbol (no prefix needed, unique)
    flag: '🇮🇳',
  },
  {
    label: 'Indonesia',
    value: 'ID',
    phoneCode: '+62',
    currency: 'IDR',
    currencySymbol: 'IDR Rp', // Prefixed Indonesian Rupiah symbol
    flag: '🇮🇩',
  },
  {
    label: 'Iran',
    value: 'IR',
    phoneCode: '+98',
    currency: 'IRR',
    currencySymbol: 'IRR ﷼', // Prefixed Iranian Rial symbol
    flag: '🇮🇷',
  },
  {
    label: 'Iraq',
    value: 'IQ',
    phoneCode: '+964',
    currency: 'IQD',
    currencySymbol: 'IQD د.ع', // Prefixed Iraqi Dinar (Arabic script)
    flag: '🇮🇶',
  },
  {
    label: 'Ireland',
    value: 'IE',
    phoneCode: '+353',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇮🇪',
  },
  {
    label: 'Israel',
    value: 'IL',
    phoneCode: '+972',
    currency: 'ILS',
    currencySymbol: '₪', // Israeli Shekel symbol (no prefix needed, unique)
    flag: '🇮🇱',
  },
  {
    label: 'Italy',
    value: 'IT',
    phoneCode: '+39',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇮🇹',
  },
  {
    label: 'Jamaica',
    value: 'JM',
    phoneCode: '+1-876',
    currency: 'JMD',
    currencySymbol: 'J$', // Prefixed Jamaican Dollar
    flag: '🇯🇲',
  },
  {
    label: 'Japan',
    value: 'JP',
    phoneCode: '+81',
    currency: 'JPY',
    currencySymbol: '¥', // Japanese Yen symbol (no prefix needed, unique)
    flag: '🇯🇵',
  },
  {
    label: 'Jordan',
    value: 'JO',
    phoneCode: '+962',
    currency: 'JOD',
    currencySymbol: 'JOD د.أ', // Prefixed Jordanian Dinar (Arabic script)
    flag: '🇯🇴',
  },
  {
    label: 'Kazakhstan',
    value: 'KZ',
    phoneCode: '+7',
    currency: 'KZT',
    currencySymbol: 'KZT ₸', // Prefixed Kazakhstani Tenge symbol
    flag: '🇰🇿',
  },
  {
    label: 'Kenya',
    value: 'KE',
    phoneCode: '+254',
    currency: 'KES',
    currencySymbol: 'KES KSh', // Prefixed Kenyan Shilling symbol
    flag: '🇰🇪',
  },
  {
    label: 'Kiribati',
    value: 'KI',
    phoneCode: '+686',
    currency: 'AUD',
    currencySymbol: 'A$', // Prefixed Australian Dollar
    flag: '🇰🇮',
  },
  {
    label: 'Kuwait',
    value: 'KW',
    phoneCode: '+965',
    currency: 'KWD',
    currencySymbol: 'KWD د.ك', // Prefixed Kuwaiti Dinar (Arabic script)
    flag: '🇰🇼',
  },
  {
    label: 'Kyrgyzstan',
    value: 'KG',
    phoneCode: '+996',
    currency: 'KGS',
    currencySymbol: 'KGS с', // Prefixed Kyrgyzstani Som symbol
    flag: '🇰🇬',
  },
  {
    label: 'Laos',
    value: 'LA',
    phoneCode: '+856',
    currency: 'LAK',
    currencySymbol: 'LAK ₭', // Prefixed Lao Kip symbol
    flag: '🇱🇦',
  },
  {
    label: 'Latvia',
    value: 'LV',
    phoneCode: '+371',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇱🇻',
  },
  {
    label: 'Lebanon',
    value: 'LB',
    phoneCode: '+961',
    currency: 'LBP',
    currencySymbol: 'LBP ل.ل', // Prefixed Lebanese Pound (Arabic script)
    flag: '🇱🇧',
  },
  {
    label: 'Lesotho',
    value: 'LS',
    phoneCode: '+266',
    currency: 'LSL',
    currencySymbol: 'LSL L', // Prefixed Lesotho Loti symbol
    flag: '🇱🇸',
  },
  {
    label: 'Liberia',
    value: 'LR',
    phoneCode: '+231',
    currency: 'LRD',
    currencySymbol: 'LR$', // Prefixed Liberian Dollar
    flag: '🇱🇷',
  },
  {
    label: 'Libya',
    value: 'LY',
    phoneCode: '+218',
    currency: 'LYD',
    currencySymbol: 'LYD د.ل', // Prefixed Libyan Dinar (Arabic script)
    flag: '🇱🇾',
  },
  {
    label: 'Liechtenstein',
    value: 'LI',
    phoneCode: '+423',
    currency: 'CHF',
    currencySymbol: 'CHF Fr', // Prefixed Swiss Franc symbol
    flag: '🇱🇮',
  },
  {
    label: 'Lithuania',
    value: 'LT',
    phoneCode: '+370',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇱🇹',
  },
  {
    label: 'Luxembourg',
    value: 'LU',
    phoneCode: '+352',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇱🇺',
  },
  {
    label: 'Madagascar',
    value: 'MG',
    phoneCode: '+261',
    currency: 'MGA',
    currencySymbol: 'MGA Ar', // Prefixed Malagasy Ariary symbol
    flag: '🇲🇬',
  },
  {
    label: 'Malawi',
    value: 'MW',
    phoneCode: '+265',
    currency: 'MWK',
    currencySymbol: 'MWK MK', // Prefixed Malawian Kwacha symbol
    flag: '🇲🇼',
  },
  {
    label: 'Malaysia',
    value: 'MY',
    phoneCode: '+60',
    currency: 'MYR',
    currencySymbol: 'RM', // Prefixed Malaysian Ringgit (commonly RM)
    flag: '🇲🇾',
  },
  {
    label: 'Maldives',
    value: 'MV',
    phoneCode: '+960',
    currency: 'MVR',
    currencySymbol: 'MVR Rf', // Prefixed Maldivian Rufiyaa symbol
    flag: '🇲🇻',
  },
  {
    label: 'Mali',
    value: 'ML',
    phoneCode: '+223',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇲🇱',
  },
  {
    label: 'Malta',
    value: 'MT',
    phoneCode: '+356',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇲🇹',
  },
  {
    label: 'Marshall Islands',
    value: 'MH',
    phoneCode: '+692',
    currency: 'USD',
    currencySymbol: 'US$', // Prefixed US Dollar
    flag: '🇲🇭',
  },
  {
    label: 'Mauritania',
    value: 'MR',
    phoneCode: '+222',
    currency: 'MRU',
    currencySymbol: 'MRU UM', // Prefixed Mauritanian Ouguiya symbol
    flag: '🇲🇷',
  },
  {
    label: 'Mauritius',
    value: 'MU',
    phoneCode: '+230',
    currency: 'MUR',
    currencySymbol: 'MUR ₨', // Prefixed Mauritian Rupee symbol
    flag: '🇲🇺',
  },
  {
    label: 'Mexico',
    value: 'MX',
    phoneCode: '+52',
    currency: 'MXN',
    currencySymbol: 'MX$', // Prefixed Mexican Peso
    flag: '🇲🇽',
  },
  {
    label: 'Micronesia',
    value: 'FM',
    phoneCode: '+691',
    currency: 'USD',
    currencySymbol: 'US$', // Prefixed US Dollar
    flag: '🇫🇲',
  },
  {
    label: 'Moldova',
    value: 'MD',
    phoneCode: '+373',
    currency: 'MDL',
    currencySymbol: 'MDL L', // Prefixed Moldovan Leu symbol
    flag: '🇲🇩',
  },
  {
    label: 'Monaco',
    value: 'MC',
    phoneCode: '+377',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇲🇨',
  },
  {
    label: 'Mongolia',
    value: 'MN',
    phoneCode: '+976',
    currency: 'MNT',
    currencySymbol: 'MNT ₮', // Prefixed Mongolian Tögrög symbol
    flag: '🇲🇳',
  },
  {
    label: 'Montenegro',
    value: 'ME',
    phoneCode: '+382',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇲🇪',
  },
  {
    label: 'Morocco',
    value: 'MA',
    phoneCode: '+212',
    currency: 'MAD',
    currencySymbol: 'MAD د.م.', // Prefixed Moroccan Dirham (Arabic script)
    flag: '🇲🇦',
  },
  {
    label: 'Mozambique',
    value: 'MZ',
    phoneCode: '+258',
    currency: 'MZN',
    currencySymbol: 'MZN MT', // Prefixed Mozambican Metical symbol
    flag: '🇲🇿',
  },
  {
    label: 'Myanmar',
    value: 'MM',
    phoneCode: '+95',
    currency: 'MMK',
    currencySymbol: 'MMK K', // Prefixed Myanmar Kyat symbol
    flag: '🇲🇲',
  },
  {
    label: 'Namibia',
    value: 'NA',
    phoneCode: '+264',
    currency: 'NAD',
    currencySymbol: 'N$', // Prefixed Namibian Dollar
    flag: '🇳🇦',
  },
  {
    label: 'Nauru',
    value: 'NR',
    phoneCode: '+674',
    currency: 'AUD',
    currencySymbol: 'A$', // Prefixed Australian Dollar
    flag: '🇳🇷',
  },
  {
    label: 'Nepal',
    value: 'NP',
    phoneCode: '+977',
    currency: 'NPR',
    currencySymbol: 'NPR ₨', // Prefixed Nepalese Rupee symbol
    flag: '🇳🇵',
  },
  {
    label: 'Netherlands',
    value: 'NL',
    phoneCode: '+31',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇳🇱',
  },
  {
    label: 'New Zealand',
    value: 'NZ',
    phoneCode: '+64',
    currency: 'NZD',
    currencySymbol: 'NZ$', // Prefixed New Zealand Dollar
    flag: '🇳🇿',
  },
  {
    label: 'Nicaragua',
    value: 'NI',
    phoneCode: '+505',
    currency: 'NIO',
    currencySymbol: 'C$', // Prefixed Nicaraguan Córdoba (C$ is common)
    flag: '🇳🇮',
  },
  {
    label: 'Niger',
    value: 'NE',
    phoneCode: '+227',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇳🇪',
  },
  {
    label: 'Nigeria',
    value: 'NG',
    phoneCode: '+234',
    currency: 'NGN',
    currencySymbol: '₦', // Nigerian Naira symbol (no prefix needed, unique)
    flag: '🇳🇬',
  },
  {
    label: 'North Korea',
    value: 'KP',
    phoneCode: '+850',
    currency: 'KPW',
    currencySymbol: 'KPW ₩', // Prefixed North Korean Won symbol
    flag: '🇰🇵',
  },
  {
    label: 'Norway',
    value: 'NO',
    phoneCode: '+47',
    currency: 'NOK',
    currencySymbol: 'NOK kr', // Prefixed Norwegian Krone symbol
    flag: '🇳🇴',
  },
  {
    label: 'Oman',
    value: 'OM',
    phoneCode: '+968',
    currency: 'OMR',
    currencySymbol: 'OMR ر.ع.', // Prefixed Omani Rial (Arabic script)
    flag: '🇴🇲',
  },
  {
    label: 'Pakistan',
    value: 'PK',
    phoneCode: '+92',
    currency: 'PKR',
    currencySymbol: 'PKR ₨', // Prefixed Pakistani Rupee symbol
    flag: '🇵🇰',
  },
  {
    label: 'Palestine',
    value: 'PS',
    phoneCode: '+970',
    currency: 'ILS',
    currencySymbol: '₪', // Israeli Shekel symbol (no prefix needed, unique)
    flag: '🇵🇸',
  },
  {
    label: 'Panama',
    value: 'PA',
    phoneCode: '+507',
    currency: 'PAB',
    currencySymbol: 'B/.', // Prefixed Panamanian Balboa (commonly B/.)
    flag: '🇵🇦',
  },
  {
    label: 'Papua New Guinea',
    value: 'PG',
    phoneCode: '+675',
    currency: 'PGK',
    currencySymbol: 'PGK K', // Prefixed Papua New Guinean Kina symbol
    flag: '🇵🇬',
  },
  {
    label: 'Paraguay',
    value: 'PY',
    phoneCode: '+595',
    currency: 'PYG',
    currencySymbol: 'PYG ₲', // Prefixed Paraguayan Guaraní symbol
    flag: '🇵🇾',
  },
  {
    label: 'Peru',
    value: 'PE',
    phoneCode: '+51',
    currency: 'PEN',
    currencySymbol: 'PEN S/', // Prefixed Peruvian Sol symbol
    flag: '🇵🇪',
  },
  {
    label: 'Philippines',
    value: 'PH',
    phoneCode: '+63',
    currency: 'PHP',
    currencySymbol: '₱', // Philippine Peso symbol (no prefix needed, unique)
    flag: '🇵🇭',
  },
  {
    label: 'Poland',
    value: 'PL',
    phoneCode: '+48',
    currency: 'PLN',
    currencySymbol: 'PLN zł', // Prefixed Polish Złoty symbol
    flag: '🇵🇱',
  },
  {
    label: 'Portugal',
    value: 'PT',
    phoneCode: '+351',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇵🇹',
  },
  {
    label: 'Qatar',
    value: 'QA',
    phoneCode: '+974',
    currency: 'QAR',
    currencySymbol: 'QAR ر.ق', // Prefixed Qatari Riyal (Arabic script)
    flag: '🇶🇦',
  },
  {
    label: 'Romania',
    value: 'RO',
    phoneCode: '+40',
    currency: 'RON',
    currencySymbol: 'RON lei', // Prefixed Romanian Leu symbol
    flag: '🇷🇴',
  },
  {
    label: 'Russia',
    value: 'RU',
    phoneCode: '+7',
    currency: 'RUB',
    currencySymbol: 'RUB ₽', // Prefixed Russian Rubles symbol
    flag: '🇷🇺',
  },
  {
    label: 'Rwanda',
    value: 'RW',
    phoneCode: '+250',
    currency: 'RWF',
    currencySymbol: 'RWF FRw', // Prefixed Rwandan Franc symbol
    flag: '🇷🇼',
  },
  {
    label: 'Samoa',
    value: 'WS',
    phoneCode: '+685',
    currency: 'WST',
    currencySymbol: 'WST T', // Prefixed Samoan Tālā symbol
    flag: '🇼🇸',
  },
  {
    label: 'Saudi Arabia',
    value: 'SA',
    phoneCode: '+966',
    currency: 'SAR',
    currencySymbol: 'SAR ر.س', // Prefixed Saudi Riyal (Arabic script)
    flag: '🇸🇦',
  },
  {
    label: 'Senegal',
    value: 'SN',
    phoneCode: '+221',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇸🇳',
  },
  {
    label: 'Serbia',
    value: 'RS',
    phoneCode: '+381',
    currency: 'RSD',
    currencySymbol: 'RSD дин.', // Prefixed Serbian Dinar symbol
    flag: '🇷🇸',
  },
  {
    label: 'Seychelles',
    value: 'SC',
    phoneCode: '+248',
    currency: 'SCR',
    currencySymbol: 'SCR ₨', // Prefixed Seychellois Rupee symbol
    flag: '🇸🇨',
  },
  {
    label: 'Sierra Leone',
    value: 'SL',
    phoneCode: '+232',
    currency: 'SLL',
    currencySymbol: 'SLL Le', // Prefixed Sierra Leonean Leone symbol
    flag: '🇸🇱',
  },
  {
    label: 'Singapore',
    value: 'SG',
    phoneCode: '+65',
    currency: 'SGD',
    currencySymbol: 'S$', // Prefixed Singapore Dollar
    flag: '🇸🇬',
  },
  {
    label: 'Slovakia',
    value: 'SK',
    phoneCode: '+421',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇸🇰',
  },
  {
    label: 'Slovenia',
    value: 'SI',
    phoneCode: '+386',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇸🇮',
  },
  {
    label: 'Solomon Islands',
    value: 'SB',
    phoneCode: '+677',
    currency: 'SBD',
    currencySymbol: 'SI$', // Prefixed Solomon Islands Dollar
    flag: '🇸🇧',
  },
  {
    label: 'Somalia',
    value: 'SO',
    phoneCode: '+252',
    currency: 'SOS',
    currencySymbol: 'SOS Sh', // Prefixed Somali Shilling symbol
    flag: '🇸🇴',
  },
  {
    label: 'South Africa',
    value: 'ZA',
    phoneCode: '+27',
    currency: 'ZAR',
    currencySymbol: 'R', // South African Rand symbol (no prefix needed, unique)
    flag: '🇿🇦',
  },
  {
    label: 'South Korea',
    value: 'KR',
    phoneCode: '+82',
    currency: 'KRW',
    currencySymbol: '₩', // South Korean Won symbol (no prefix needed, unique)
    flag: '🇰🇷',
  },
  {
    label: 'South Sudan',
    value: 'SS',
    phoneCode: '+211',
    currency: 'SSP',
    currencySymbol: 'SSP £', // Prefixed South Sudanese Pound
    flag: '🇸🇸',
  },
  {
    label: 'Spain',
    value: 'ES',
    phoneCode: '+34',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇪🇸',
  },
  {
    label: 'Sri Lanka',
    value: 'LK',
    phoneCode: '+94',
    currency: 'LKR',
    currencySymbol: 'Rs', // Sri Lankan Rupee symbol (no prefix needed, unique)
    flag: '🇱🇰',
  },
  {
    label: 'Sudan',
    value: 'SD',
    phoneCode: '+249',
    currency: 'SDG',
    currencySymbol: 'SDG ج.س.', // Prefixed Sudanese Pound (Arabic script)
    flag: '🇸🇩',
  },
  {
    label: 'Suriname',
    value: 'SR',
    phoneCode: '+597',
    currency: 'SRD',
    currencySymbol: 'SRD $', // Prefixed Surinamese Dollar
    flag: '🇸🇷',
  },
  {
    label: 'Sweden',
    value: 'SE',
    phoneCode: '+46',
    currency: 'SEK',
    currencySymbol: 'kr', // Swedish Krona symbol (no prefix needed, unique)
    flag: '🇸🇪',
  },
  {
    label: 'Switzerland',
    value: 'CH',
    phoneCode: '+41',
    currency: 'CHF',
    currencySymbol: 'Fr.', // Swiss Franc symbol (no prefix needed, unique)
    flag: '🇨🇭',
  },
  {
    label: 'Syria',
    value: 'SY',
    phoneCode: '+963',
    currency: 'SYP',
    currencySymbol: 'SYP £', // Prefixed Syrian Pound
    flag: '🇸🇾',
  },
  {
    label: 'Taiwan',
    value: 'TW',
    phoneCode: '+886',
    currency: 'TWD',
    currencySymbol: 'NT$', // Prefixed New Taiwan Dollar
    flag: '🇹🇼',
  },
  {
    label: 'Tajikistan',
    value: 'TJ',
    phoneCode: '+992',
    currency: 'TJS',
    currencySymbol: 'TJS ЅМ', // Prefixed Tajikistani Somoni symbol
    flag: '🇹🇯',
  },
  {
    label: 'Tanzania',
    value: 'TZ',
    phoneCode: '+255',
    currency: 'TZS',
    currencySymbol: 'TSh', // Prefixed Tanzanian Shilling
    flag: '🇹🇿',
  },
  {
    label: 'Thailand',
    value: 'TH',
    phoneCode: '+66',
    currency: 'THB',
    currencySymbol: '฿', // Thai Baht symbol (no prefix needed, unique)
    flag: '🇹🇭',
  },
  {
    label: 'Togo',
    value: 'TG',
    phoneCode: '+228',
    currency: 'XOF',
    currencySymbol: 'XOF CFA', // Prefixed West African CFA Franc
    flag: '🇹🇬',
  },
  {
    label: 'Tonga',
    value: 'TO',
    phoneCode: '+676',
    currency: 'TOP',
    currencySymbol: 'T$', // Prefixed Tongan Paʻanga
    flag: '🇹🇴',
  },
  {
    label: 'Trinidad and Tobago',
    value: 'TT',
    phoneCode: '+1-868',
    currency: 'TTD',
    currencySymbol: 'TT$', // Prefixed Trinidad and Tobago Dollar
    flag: '🇹🇹',
  },
  {
    label: 'Tunisia',
    value: 'TN',
    phoneCode: '+216',
    currency: 'TND',
    currencySymbol: 'TND د.ت', // Prefixed Tunisian Dinar (Arabic script)
    flag: '🇹🇳',
  },
  {
    label: 'Turkey',
    value: 'TR',
    phoneCode: '+90',
    currency: 'TRY',
    currencySymbol: '₺', // Turkish Lira symbol (no prefix needed, unique)
    flag: '🇹🇷',
  },
  {
    label: 'Turkmenistan',
    value: 'TM',
    phoneCode: '+993',
    currency: 'TMT',
    currencySymbol: 'TMT m', // Prefixed Turkmenistani Manat symbol
    flag: '🇹🇲',
  },
  {
    label: 'Tuvalu',
    value: 'TV',
    phoneCode: '+688',
    currency: 'AUD',
    currencySymbol: 'A$', // Prefixed Australian Dollar
    flag: '🇹🇻',
  },
  {
    label: 'Uganda',
    value: 'UG',
    phoneCode: '+256',
    currency: 'UGX',
    currencySymbol: 'UGX Sh', // Prefixed Ugandan Shilling symbol
    flag: '🇺🇬',
  },
  {
    label: 'Ukraine',
    value: 'UA',
    phoneCode: '+380',
    currency: 'UAH',
    currencySymbol: '₴', // Ukrainian Hryvnia symbol (no prefix needed, unique)
    flag: '🇺🇦',
  },
  {
    label: 'United Arab Emirates',
    value: 'AE',
    phoneCode: '+971',
    currency: 'AED',
    currencySymbol: 'د.إ', // UAE Dirham symbol (no prefix needed, unique)
    flag: '🇦🇪',
  },
  {
    label: 'United Kingdom',
    value: 'GB',
    phoneCode: '+44',
    currency: 'GBP',
    currencySymbol: '£', // British Pound symbol (no prefix needed, unique)
    flag: '🇬🇧',
  },
  {
    label: 'United States',
    value: 'US',
    phoneCode: '+1',
    currency: 'USD',
    currencySymbol: '$', // US Dollar symbol (no prefix needed, unique)
    flag: '🇺🇸',
  },
  {
    label: 'Uruguay',
    value: 'UY',
    phoneCode: '+598',
    currency: 'UYU',
    currencySymbol: '$U', // Prefixed Uruguayan Peso
    flag: '🇺🇾',
  },
  {
    label: 'Uzbekistan',
    value: 'UZ',
    phoneCode: '+998',
    currency: 'UZS',
    currencySymbol: 'UZS soʻm', // Prefixed Uzbekistani Som symbol
    flag: '🇺🇿',
  },
  {
    label: 'Vanuatu',
    value: 'VU',
    phoneCode: '+678',
    currency: 'VUV',
    currencySymbol: 'VT', // Vanuatu Vatu symbol (no prefix needed, unique)
    flag: '🇻🇺',
  },
  {
    label: 'Vatican City',
    value: 'VA',
    phoneCode: '+379',
    currency: 'EUR',
    currencySymbol: '€', // Euro symbol (no prefix needed)
    flag: '🇻🇦',
  },
  {
    label: 'Venezuela',
    value: 'VE',
    phoneCode: '+58',
    currency: 'VES',
    currencySymbol: 'Bs.', // Prefixed Venezuelan Bolívar
    flag: '🇻🇪',
  },
  {
    label: 'Vietnam',
    value: 'VN',
    phoneCode: '+84',
    currency: 'VND',
    currencySymbol: '₫', // Vietnamese Dong symbol (no prefix needed, unique)
    flag: '🇻🇳',
  },
  {
    label: 'Yemen',
    value: 'YE',
    phoneCode: '+967',
    currency: 'YER',
    currencySymbol: 'YER ﷼', // Prefixed Yemeni Rial symbol
    flag: '🇾🇪',
  },
  {
    label: 'Zambia',
    value: 'ZM',
    phoneCode: '+260',
    currency: 'ZMW',
    currencySymbol: 'ZK', // Prefixed Zambian Kwacha
    flag: '🇿🇲',
  },
  {
    label: 'Zimbabwe',
    value: 'ZW',
    phoneCode: '+263',
    currency: 'ZWL',
    currencySymbol: 'Z$', // Prefixed Zimbabwean Dollar
    flag: '🇿🇼',
  },
];

export type Currency = {
  icon: ReactNode | string | { uri: string };
  value: string;
  label: string;
  type: 'fiat' | 'crypto';
};

